import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { getOrganizationGroupsCacheKey, longTermCacheStaleTime, onUpdateOrganizationGroups } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';
import { useMemo } from 'react';

const intl = getIntl('UseOrganizationGroups', {
    groupsLoadFailure: 'Failed to load groups',
    createGroupFailure: 'Failed to create group',
    createGroupSuccess: 'Group created successfully',
    updateGroupFailure: 'Failed to update group',
    updateGroupSuccess: 'Group updated successfully',
    deleteGroupFailure: 'Failed to delete group',
    deleteGroupSuccess: 'Group deleted successfully',
    deleteGroupsSuccess: 'Groups deleted successfully',
    deleteGroupsFailure: 'Failed to delete all groups',
});

export type UseOrganizationGroupsArgs = {
    onCreateGroupSuccess?: () => void;
    onUpdateGroupSuccess?: () => void;
    onDeleteGroupSuccess?: () => void;
    onDeleteGroupsSuccess?: () => void;
    groupId?: string;
};

export type UpdateArgs = {
    groupId: string;
    group: APITypes.organizationService.UpdateOrganizationGroupRequest;
};

export type UseOrganizationGroupsReturnValue = {
    groups: APITypes.organizationService.ListOrganizationGroupsResponse['items'];
    isLoadingGroups: boolean;
    isFetchingGroups: boolean;
    createGroup: (group: APITypes.organizationService.CreateOrganizationGroupRequest) => void;
    isCreatingGroup: boolean;
    updateGroup: (args: UpdateArgs) => void;
    isUpdatingGroup: boolean;
    deleteGroup: (groupId: string) => void;
    isDeletingGroup: boolean;
    deleteGroups: (groupIds: string[]) => void;
    isDeletingGroups: boolean;
    group: APITypes.organizationService.OrganizationGroupDetails | null;
    isLoadingGroup: boolean;
};

const emptyArray: any[] = [];

export default function useOrganizationGroups({
    onCreateGroupSuccess,
    onUpdateGroupSuccess,
    onDeleteGroupSuccess,
    onDeleteGroupsSuccess,
    groupId,
}: UseOrganizationGroupsArgs): UseOrganizationGroupsReturnValue {
    const organizationId = useOrganizationId();
    const notify = useNotify();
    const organizationGroupsQuery = useQuery({
        queryKey: getOrganizationGroupsCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.ListOrganizationGroupsResponse>(
                urls.api.organizations.groups.groups(organizationId),
            );
        },
        staleTime: longTermCacheStaleTime,
    });
    useQueryErrorNotify(organizationGroupsQuery, intl('groupsLoadFailure'));

    const createGroupMutation = useMutation({
        mutationFn: async (group: APITypes.organizationService.CreateOrganizationGroupRequest) => {
            return await appAPI.post(urls.api.organizations.groups.groups(organizationId), {
                body: group,
            });
        },
        onSuccess: () => {
            onUpdateOrganizationGroups.resetCache({})();
            notify('success', intl('createGroupSuccess'));
            onCreateGroupSuccess?.();
        },
        onError: () => {
            notify('error', intl('createGroupFailure'));
        },
    });

    const updateGroupMutation = useMutation({
        mutationFn: async (args: UpdateArgs) => {
            return await appAPI.put(urls.api.organizations.groups.group(organizationId, args.groupId), {
                body: args.group,
            });
        },
        onSuccess: () => {
            onUpdateOrganizationGroups.resetCache({})();
            notify('success', intl('updateGroupSuccess'));
            onUpdateGroupSuccess?.();
        },
        onError: () => {
            notify('error', intl('updateGroupFailure'));
        },
    });

    const deleteGroupMutation = useMutation({
        mutationFn: async (groupId: string) => {
            return await appAPI.del(urls.api.organizations.groups.group(organizationId, groupId));
        },
        onSuccess: () => {
            onUpdateOrganizationGroups.resetCache({})();
            notify('success', intl('deleteGroupSuccess'));
            onDeleteGroupSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteGroupFailure'));
        },
    });

    const deleteGroupsMutation = useMutation({
        mutationFn: async (groupIds: string[]) => {
            for (const groupId of groupIds) {
                await appAPI.del(urls.api.organizations.groups.group(organizationId, groupId));
            }
        },
        onSuccess: () => {
            onUpdateOrganizationGroups.resetCache({})();
            notify('success', intl('deleteGroupsSuccess'));
            onDeleteGroupsSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteGroupsFailure'));
        },
    });

    const group = useMemo(() => {
        return organizationGroupsQuery.data?.items?.find((group) => group.organizationGroupId === groupId) || null;
    }, [organizationGroupsQuery.data, groupId]);

    return {
        groups: organizationGroupsQuery.data?.items || emptyArray,
        isLoadingGroups: organizationGroupsQuery.isLoading,
        isFetchingGroups: organizationGroupsQuery.isFetching,
        createGroup: createGroupMutation.mutate,
        isCreatingGroup: createGroupMutation.isPending,
        updateGroup: updateGroupMutation.mutate,
        isUpdatingGroup: updateGroupMutation.isPending,
        deleteGroup: deleteGroupMutation.mutate,
        isDeletingGroup: deleteGroupMutation.isPending,
        deleteGroups: deleteGroupsMutation.mutate,
        isDeletingGroups: deleteGroupsMutation.isPending,
        group: group,
        isLoadingGroup: organizationGroupsQuery.isLoading,
    };
}
